export type Procedure = { id: string; updatedAt: string } & ProcedureDetail;

export type ProcedureDetail = {
  jurisdiction: Jurisdiction;

  procedureTags: ProcedureTagBaseInfo[];

  name: string;
  title?: string;
  summary: string;
  description?: string;
  notice?: string;
  descriptionFiles?: DescriptionFile[];

  regions: Region[];

  target?: string;
  detailedRequirement?: string;
  trigger?: string;
  deadline?: string;

  acceptAppOnline: boolean;
  onlineInfo?: OnlineInfo;

  acceptAppByMail: boolean;
  byMailInfo?: ByMailInfo;

  acceptAppInPerson: boolean;
  inPersonInfo?: InPersonInfo;

  acceptIssueAtStore: boolean;
  atStoreInfo?: AtStoreInfo;

  references?: Reference[];

  issueFiles?: IssueFile[];

  contacts?: Contact[];

  contentStatus: StatusValue;
};

export type Jurisdiction = {
  organizationId: string;
  groupId?: string;
  groupName?: string;
};

export type ProcedureTagBaseInfo = {
  id: string;
  name: string;
  priority: number;
  suppressOnPortal: boolean;
};

export type DescriptionFile = {
  name: string;
  filePath?: string; // 下書き時、空白を許容
  type?: string;
  fileName: string;
};

export type Region = {
  id?: string; // 下書き時、空白を許容
  name: string;
};

export type OnlineInfo = {
  applicants: string[];
  applicationUrl?: string;
  notice?: string;
  grafferSmartApplyInfo?: GrafferSmartApplyInfo;
  acceptAppByProxyIf?: string;
  fee?: string;
};

export type GrafferSmartApplyInfo = {
  procedureId: string;
  procedurePath: string;
  procedureName: string;
};

export type ByMailInfo = {
  applicants?: string[];
  destinations: Destination[];
  submissions?: Submission[];
  documents: ProcDocument[];
  acceptAppByProxyIf?: string;
  fee?: string;
};

export type Destination = {
  countryCode: string;
  postalCode: string;
  address1: string;
  address2: string;
  address3?: string;
  name: string;
  tel?: string;
  usedIf?: string[];
};

export type InPersonInfo = {
  applicants?: string[];
  belongings?: Belonging[];
  documents: ProcDocument[];
  places?: PlaceForProc[];
  genericPlaces?: string[];
  placeHelpMessage?: string;
  acceptAppByProxyIf?: string;
  fee?: string;
};

export type Submission = Belonging;

export type ProcDocument = {
  name: string;
  filePath?: string;
  type?: string;
  fileName: string;
  description?: string;
  helpUrl?: string;
  requireIf?: string[];
};

export type Belonging = {
  name: string;
  helpUrl?: string;
  description?: string;
  requireIf?: string[];
};

export type PlaceForProc = {
  id?: string;
  name: string;
  counterName?: string;
  address?: Address;
  tel?: string;
  receptionHours?: string;
  usedIf?: string[];
};

export type Address = {
  postalCode: string; // 郵便番号
  address1: string; // 住所 - 丁目番地号より前まで
  address2: string; // 住所 - 丁目番地号
  address3?: string; // 住所 - ビル名部屋番号等
};

export type AtStoreInfo = {
  notice?: string;
  fee?: string;
};

export type Reference = {
  name: string;
  url: string;
};

export type IssueFile = {
  name: string;
  description?: string;
  notice?: string;
};

export type Contact = {
  name: string;
  tel: string;
  fax?: string;
  email?: string;
  usedIf?: string[];
};

export const Status = {
  Draft: "DRAFT",
  Published: "PUBLISHED",
  Archived: "ARCHIVED",
} as const;

export const StatusList = [
  Status.Draft,
  Status.Published,
  Status.Archived,
] as const;

export type StatusValue = (typeof Status)[keyof typeof Status];

// as master
export type Place = {
  id: string;
  name: string;
  organizationId: string;
  address?: Address;
  tel?: string;
  receptionHours?: string;
};

// as master
export type ProcedureTag = ProcedureTagBaseInfo & {
  organizationId: string;
};

export type ProcValidation = {
  errorCounts: number;

  name?: string;
  title?: string;
  summary?: string;
  description?: string;
  notice?: string;
  descriptionFiles?: DescriptionFileValidation[];
  procedureTags?: string[];

  target?: string;
  detailedRequirement?: string;
  trigger?: string;
  deadline?: string;

  acceptAppOnline?: string;
  onlineInfo?: OnlineInfoValidation;

  acceptAppByMail?: string;
  byMailInfo?: ByMailInfoValidation;

  acceptAppInPerson?: string;
  inPersonInfo?: InPersonInfoValidation;

  acceptIssueAtStore?: string;
  atStoreInfo?: AtStoreInfoValidation;

  references?: ReferenceValidation[];

  issueFiles?: IssueFileValidation[];

  contacts?: ContactValidation[];

  jurisdictionGrpName?: string;
};

export type OnlineInfoValidation = {
  applicants?: string[];
  applicationUrl?: string;
  notice?: string;
  grafferSmartApplyInfo?: string;
  acceptAppByProxyIf?: string;
  fee?: string;
};

export type ByMailInfoValidation = {
  applicants?: string[];
  destinations?: DestinationValidation[];
  submissions?: SubmissionValidation[];
  documents?: ProcDocument[];
  acceptAppByProxyIf?: string;
  fee?: string;
};

export type InPersonInfoValidation = {
  applicants?: string[];
  belongings?: BelongingValidation[];
  documents?: ProcDocument[];
  places?: PlaceForProcValidation[];
  genericPlaces?: string[];
  placeHelpMessage?: string;
  acceptAppByProxyIf?: string;
  fee?: string;
};

export type AtStoreInfoValidation = {
  notice?: string;
  fee?: string;
};

export type DescriptionFileValidation = {
  name?: string;
  file?: string;
};

export type ReferenceValidation = {
  name?: string;
  url?: string;
};

export type IssueFileValidation = {
  name?: string;
  description?: string;
  notice?: string;
};

export type ContactValidation = {
  name?: string;
  tel?: string;
  fax?: string;
  email?: string;
  usedIf?: string[];
};

export type PlaceForProcValidation = {
  id?: string;
  counterName?: string;
  // address?: AddressValidation;
  // tel?: string;
  // receptionHours?: string;
  usedIf?: string[];
};

export type AddressValidation = {
  postalCode?: string;
  address1?: string;
  address2?: string;
  address3?: string;
};

export type DestinationValidation = {
  countryCode?: string;
  postalCode?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  name?: string;
  tel?: string;
  usedIf?: string[];
};

export type SubmissionValidation = BelongingValidation;

export type DocumentValidation = {
  name?: string;
  file?: string;
  description?: string;
  helpUrl?: string;
  requireIf?: string[];
};

export type BelongingValidation = {
  name?: string;
  helpUrl?: string;
  description?: string;
  requireIf?: string[];
};

export type SmartApplyValidationResult = {
  error?: string;
};

export type searchModeCode = keyof typeof searchMode;

export const searchMode = {
  PROC_MANAGEMENT: "proc-management",
  SMART_APPLY: "smart-apply",
  HYBRID: "hybrid",
} as const;

export type ProcManagementConfig = {
  searchConfig: {
    searchMode: searchModeCode | string;
  };
};
